// frontend/src/GothamPostGameCount/csvCounts.js — reads counts from an uploaded CSV. A row matches a size by barcode,
// then SKU, then product + size name (a blank size is "One size"). The exported sheet works as-is: its Counted column
// is read; so does any CSV with a counted column and one of those to match on. Nothing here talks to Shopify.

export function parseCsv(text) {
  const rows = []; let row = []; let cell = ''; let q = false;
  const s = text.replace(/^﻿/, '');
  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (q) {
      if (ch === '"' && s[i + 1] === '"') { cell += '"'; i += 1; } else if (ch === '"') q = false; else cell += ch;
    } else if (ch === '"') q = true;
    else if (ch === ',') { row.push(cell); cell = ''; } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && s[i + 1] === '\n') i += 1;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}

const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
const COLS = {
  counted: ['counted', 'count', 'qty counted', 'quantity', 'qty', 'on hand'],
  barcode: ['barcode', 'upc'],
  sku: ['sku'],
  product: ['product', 'product title', 'title', 'item'],
  size: ['size', 'variant', 'variant title'],
};

// products: the sheet's products. Returns { error } or { matched: [{ id, n }], unmatched, bad, dupes, blank }, with rows
// numbered as a spreadsheet shows them (the header is row 1).
export function readCounts(text, fileName, products) {
  const rows = parseCsv(text);
  if (!rows.length) return { error: `${fileName} is empty.` };
  const head = rows[0].map(norm);
  const col = Object.fromEntries(Object.entries(COLS).map(([k, names]) => [k, head.findIndex((h) => names.includes(h))]));
  if (col.counted < 0) return { error: `No "Counted" column in ${fileName}. Add a column named Counted with the numbers you counted.` };
  if (col.barcode < 0 && col.sku < 0 && col.product < 0) return { error: `${fileName} needs a Barcode, SKU or Product column to match each row to a size.` };
  const byBarcode = {}; const bySku = {}; const byName = {};
  products.forEach((p) => p.variants.forEach((v) => {
    if (v.barcode) byBarcode[norm(v.barcode)] = v.id;
    if (v.sku) bySku[norm(v.sku)] = v.id;
    byName[`${norm(p.title)}|${norm(v.title)}`] = v.id;
  }));
  const found = {}; const unmatched = []; const bad = []; const dupes = []; let blank = 0;
  rows.slice(1).forEach((r, i) => {
    const line = i + 2; const get = (j) => (j >= 0 ? (r[j] || '').trim() : '');
    const raw = get(col.counted);
    const label = [get(col.product), get(col.size)].filter(Boolean).join(', ') || get(col.barcode) || get(col.sku) || `row ${line}`;
    if (raw === '') { blank += 1; return; }          // left blank: skipped, never read as 0
    if (!/^\d+$/.test(raw)) { bad.push({ line, label, raw }); return; }
    const id = (get(col.barcode) && byBarcode[norm(get(col.barcode))]) || (get(col.sku) && bySku[norm(get(col.sku))])
      || byName[`${norm(get(col.product))}|${norm(get(col.size) || 'One size')}`];
    if (!id) { unmatched.push({ line, label }); return; }
    if (found[id] !== undefined) dupes.push({ line, label });
    found[id] = Number(raw);
  });
  return { matched: Object.entries(found).map(([id, n]) => ({ id, n })), unmatched, bad, dupes, blank };
}
